import { useEffect, useRef, useCallback, useState } from 'react';

export interface UseIdleTimerOptions {
  /** Timeout in milliseconds before user is considered idle */
  timeout: number;
  /** Callback when user becomes idle (timeout reached) */
  onIdle: () => void;
  /** Optional callback when warning threshold is reached */
  onWarning?: () => void;
  /** Time in milliseconds before timeout to trigger warning (default: 60000 = 1 minute) */
  warningTime?: number;
  /** Whether the timer is enabled (default: true) */
  enabled?: boolean;
  /** Events to track for activity (default: common user interactions) */
  events?: string[];
}

export interface UseIdleTimerReturn {
  /** Manually reset the idle timer */
  resetTimer: () => void;
  /** Whether the user is currently idle */
  isIdle: boolean;
  /** Whether the warning is currently showing */
  isWarning: boolean;
  /** Remaining time in milliseconds until idle */
  remainingTime: number;
  /** Pause the idle timer */
  pause: () => void;
  /** Resume the idle timer */
  resume: () => void;
  /** Whether the timer is currently paused */
  isPaused: boolean;
}

const DEFAULT_EVENTS = [
  'mousedown',
  'mousemove',
  'keydown',
  'scroll',
  'touchstart',
  'click',
  'keypress',
  'wheel',
  'resize',
  'visibilitychange',
];

/**
 * Hook to detect user inactivity and trigger callbacks
 * 
 * @example
 * ```tsx
 * const { isWarning, remainingTime, resetTimer } = useIdleTimer({
 *   timeout: 15 * 60 * 1000, // 15 minutes
 *   warningTime: 60 * 1000, // 1 minute warning
 *   onWarning: () => setShowWarning(true),
 *   onIdle: () => logout(),
 * });
 * ```
 */
export const useIdleTimer = ({
  timeout,
  onIdle,
  onWarning,
  warningTime = 60000,
  enabled = true,
  events = DEFAULT_EVENTS,
}: UseIdleTimerOptions): UseIdleTimerReturn => {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warningRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastActivityRef = useRef<number>(Date.now());
  
  const [isIdle, setIsIdle] = useState(false);
  const [isWarning, setIsWarning] = useState(false);
  const [remainingTime, setRemainingTime] = useState(timeout);
  const [isPaused, setPaused] = useState(false);

  const clearTimers = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (warningRef.current) {
      clearTimeout(warningRef.current);
      warningRef.current = null;
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const resetTimer = useCallback(() => {
    if (!enabled || isPaused) return;
    
    clearTimers();
    lastActivityRef.current = Date.now();
    setIsIdle(false);
    setIsWarning(false);
    setRemainingTime(timeout);

    // Set up warning timer if onWarning callback is provided
    if (onWarning && warningTime > 0 && warningTime < timeout) {
      warningRef.current = setTimeout(() => {
        setIsWarning(true);
        onWarning();
      }, timeout - warningTime);
    }

    // Set up idle timer
    timeoutRef.current = setTimeout(() => {
      setIsIdle(true);
      setIsWarning(false);
      onIdle();
    }, timeout);

    // Update remaining time every second
    intervalRef.current = setInterval(() => {
      const elapsed = Date.now() - lastActivityRef.current;
      const remaining = Math.max(0, timeout - elapsed);
      setRemainingTime(remaining);
    }, 1000);
  }, [enabled, isPaused, timeout, warningTime, onIdle, onWarning, clearTimers]);

  const pause = useCallback(() => {
    setPaused(true);
    clearTimers();
  }, [clearTimers]);

  const resume = useCallback(() => {
    setPaused(false);
    resetTimer();
  }, [resetTimer]);

  // Handle activity events
  useEffect(() => {
    if (!enabled) return;

    const handleActivity = (event: Event) => {
      // For visibility change, only reset if document becomes visible
      if (event.type === 'visibilitychange' && document.hidden) {
        return;
      }
      
      if (!isPaused) {
        resetTimer();
      }
    };

    // Add event listeners
    events.forEach(event => {
      if (event === 'visibilitychange') {
        document.addEventListener(event, handleActivity);
      } else {
        window.addEventListener(event, handleActivity, { passive: true });
      }
    });

    // Start the timer initially
    resetTimer();

    // Cleanup
    return () => {
      events.forEach(event => {
        if (event === 'visibilitychange') {
          document.removeEventListener(event, handleActivity);
        } else {
          window.removeEventListener(event, handleActivity);
        }
      });
      clearTimers();
    };
  }, [enabled, events, isPaused, resetTimer, clearTimers]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearTimers();
    };
  }, [clearTimers]);

  return {
    resetTimer,
    isIdle,
    isWarning,
    remainingTime,
    pause,
    resume,
    isPaused,
  };
};

export default useIdleTimer;
